/**
 * NavTreeMenus — Renderer für die Desktop-Dropdowns + das Mobile-Menü aus
 * `NAV_TREE` (M-UX-1.2, `architecture-ux-restructure.md` §4.1).
 *
 * Ersetzt das handkodierte Doppel-Markup (Desktop-Dropdown + separates
 * Mobile-Duplikat) in `MainLayout.tsx` durch EINE datengetriebene Ableitung
 * aus `resolveVisibleTree(ctx, role)`. Dieses Modul rendert nur — es enthält
 * KEINE Gate-Logik (die lebt vollständig in `config/navigation.ts`, W1.1) und
 * KEINEN `NavContext`-Aufbau (der lebt in `MainLayout`, wo die zugrunde
 * liegenden Hooks — `useAuth`, `useIsReadOnly`, `useIsAdvisorContext` —
 * bereits vorhanden sind).
 *
 * Bausteine (bewusst einzeln exportiert, damit der Tester `visible`/`locked`/
 * `hidden` isoliert prüfen kann, ohne `MainLayout`/Firebase mitzuziehen):
 * - {@link isNavItemActive} / {@link isNavSectionActive} — reine Aktiv-Zustand-
 *   Auflösung (Fix Z2: `activeExact` ⇒ nur exakter Pfad-Match).
 * - {@link NavItemLink} — EIN Nav-Eintrag; rendert `visible`/`locked` als Link,
 *   `hidden` als `null` (§4.1: „filtert hidden, behält locked" — diese
 *   Komponente ist die defensive zweite Prüfung, falls sie je außerhalb von
 *   `resolveVisibleTree` mit einem ungefilterten Item aufgerufen wird).
 * - {@link DesktopNavDropdown} / {@link MobileNavGroup} — EINE `NavSection` als
 *   Desktop-Dropdown bzw. Mobile-Gruppe, inkl. optionaler `extraItems`
 *   (nicht-Tree-Einträge wie den Logout-Button) und `excludeItemIds`
 *   (Items, deren Verhalten bewusst außerhalb des generischen Renderers
 *   abgebildet wird — s. `MainLayout.tsx`-Kommentar zu `subscriptionUpgrade`).
 *
 * `locked`-Klick-Verhalten (§4.1, verbindlich): navigiert normal zur Route
 * (dort greift künftig der Teaser aus M-UX-G2) — es wird NICHT auf eine
 * separate Upgrade-Seite umgeleitet. Die Schloss-Affordance ist rein visuell
 * (Icon + Tooltip/aria-label).
 *
 * Tarif im `locked`-Hinweis (F-2, Re-Loop 2): `title`/`aria-label` des
 * Schloss-Eintrags lauteten hartkodiert `` `${label} – Premium` `` — für JEDES
 * gesperrte Item, auch die fünf Ultra-Features (Belegscanner, Bank-Abgleich,
 * DATEV-Export und die zwei in diesem PR neu erzeugten `locked`-Zustände
 * Reisekosten/UStVA). Der i18n-Key `lockedFeatureHint` existierte in KEINER
 * Locale, der Default griff also immer. Ergebnis: „Reisekosten – Premium" im
 * Tooltip UND im Screenreader — exakt der Fehlertyp, den dieser PR beseitigt.
 * Der Tarif kommt jetzt aus `getPromotionTargetTier(item.lockedPromotionId)`,
 * also aus DERSELBEN Tabelle wie der `?tier=`-Parameter der Upgrade-Hrefs
 * (D-1) — keine zweite Quelle für Tarif-Zuordnung.
 *
 * @module components/layout/NavTreeMenus
 */

import React from 'react';
import type { ComponentType, ReactNode, RefObject } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconChevronDown, IconLock } from '../../icons';
import type { NavBadgeId, NavContext, NavItem, NavSection } from '../../config/navigation';
import { getPromotionTargetTier } from '../../config/nudgePromotions';

/** Mapping `badgeId → Badge-Komponente` (§4.1 Design-Entscheidung 2). Optional — kein NAV_TREE-Item nutzt `badge` in W1, das Mapping ist für spätere Wellen (ab W5 `inboxNew`) vorbereitet. */
export type NavBadgeRegistry = Partial<Record<NavBadgeId, ComponentType>>;

/**
 * Reine Aktiv-Zustand-Prüfung für EIN Item (Fix Z2, §4.1): `activeExact`
 * erzwingt exakten Pfad-Match, sonst Prefix-Match (`route` oder `route/…`).
 *
 * `ctx` ist OPTIONAL (architecture-cockpit-route-split.md §5.4, M-CRS-4):
 * ist es gesetzt UND das Item trägt ein `activeAlsoWhen`-Alias, gilt das Item
 * zusätzlich als aktiv, wenn das Alias `true` liefert. Bestehende 2-Arg-Aufrufe
 * (z. B. `isNavSectionActive`) bleiben kompilierfähig und verhalten sich
 * unverändert (Null-Delta), weil `activeAlsoWhen` ohne `ctx` nie ausgewertet
 * wird.
 */
export function isNavItemActive(
  pathname: string,
  item: Pick<NavItem, 'route' | 'activeExact' | 'activeAlsoWhen'>,
  ctx?: NavContext,
): boolean {
  const pathMatch = item.activeExact
    ? pathname === item.route
    : pathname === item.route || pathname.startsWith(`${item.route}/`);
  if (pathMatch) return true;
  return ctx !== undefined && item.activeAlsoWhen?.(pathname, ctx) === true;
}

/**
 * Reine Aktiv-Zustand-Prüfung für eine ganze Item-Liste (Dropdown-Trigger-
 * Hervorhebung). `ctx` ist OPTIONAL (M-CRS-4, §5.4) und wird an
 * {@link isNavItemActive} durchgereicht, damit ein `activeAlsoWhen`-Alias eines
 * Items (z. B. `createInvoice` auf `/dashboard`) auch den Sektions-Trigger
 * hervorhebt. Ohne `ctx` unverändertes Verhalten (Null-Delta).
 */
export function isNavSectionActive(
  pathname: string,
  items: readonly NavItem[],
  ctx?: NavContext,
): boolean {
  return items.some((item) => isNavItemActive(pathname, item, ctx));
}

interface NavItemLinkProps {
  item: NavItem;
  ctx: NavContext;
  variant: 'desktop' | 'mobile' | 'sidebar';
  onNavigate?: () => void;
  highlightActive?: boolean;
  badgeRegistry?: NavBadgeRegistry;
}

/**
 * EIN NavItem, aufgelöst über `item.visibility(ctx)`:
 * - `hidden` → `null` (defensive zweite Prüfung, s. Modul-Kopf).
 * - `locked` → Link zur Route (§4.1: niemals ins Leere) + Schloss-Icon +
 *   `title`/`aria-label` als Upsell-Hinweis.
 * - `visible` → normaler Link.
 *
 * `data-nav-id={item.id}` ist gesetzt für E2E-Selektoren (§4.1/architecture
 * Playwright-Gate).
 */
export const NavItemLink: React.FC<NavItemLinkProps> = ({
  item,
  ctx,
  variant,
  onNavigate,
  highlightActive = false,
  badgeRegistry,
}) => {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const visibility = item.visibility(ctx);
  if (visibility === 'hidden') return null;

  const isLocked = visibility === 'locked';
  // M-CRS-4 (§5.4): `ctx` durchreichen, damit das `activeAlsoWhen`-Alias von
  // `createInvoice` greift (normaler Nutzer auf `/dashboard`/Formular behält das
  // Highlight, obwohl die Route jetzt konstant auf `/rechnungen/neu` zeigt).
  const active = highlightActive && isNavItemActive(location.pathname, item, ctx);
  const Icon = item.icon;
  const BadgeComponent = item.badge ? badgeRegistry?.[item.badge] : undefined;
  const label = t(item.labelKey);
  // F-2: Tarif-Name aus der EINEN Zuordnungstabelle (`NUDGE_PROMOTION_TARGET_TIER`)
  // statt hartkodiert. Fehlt `lockedPromotionId` an einem lockable Item, wird
  // KEIN Tarif geraten — dann greift der tarifneutrale Hinweis. Beide Varianten
  // tragen ihre Platzhalter auch im `defaultValue`, sodass eine fehlende
  // Übersetzung nie still einen falschen Tarif produziert.
  const lockedTier = item.lockedPromotionId ? getPromotionTargetTier(item.lockedPromotionId) : undefined;
  const lockedHint = lockedTier
    ? t('lockedFeatureHint', {
        ns: 'common',
        feature: label,
        plan: t(`tierName.${lockedTier}`, { ns: 'common', defaultValue: lockedTier }),
        defaultValue: '{{feature}} – nur mit {{plan}}',
      })
    : t('lockedFeatureHintGeneric', {
        ns: 'common',
        feature: label,
        defaultValue: '{{feature}} – kostenpflichtiger Tarif',
      });

  const desktopClassName = `flex items-center px-4 py-2 text-sm ${
    active ? 'bg-indigo-600 text-[#0C1322]' : 'text-secondary hover:bg-surface-inset hover:text-primary'
  }`;
  const mobileClassName = 'flex items-center pl-5 pr-2 py-2 text-sm text-secondary hover:bg-surface-raised';
  // M-UX-3.1 (`plan-ux-3.1-sidebar.md` §2): vertikaler Nav-Link für den
  // persistenten Sidebar — aktiver Zustand mint/indigo hervorgehoben (analog
  // zum Desktop-Dropdown-Item), sonst dezentes Grau. Nur wirksam bei
  // `variant==='sidebar'`; `desktop`/`mobile` bleiben unverändert.
  const sidebarClassName = `flex items-center px-3 py-2 text-sm rounded-md ${
    active ? 'bg-indigo-600 text-[#0C1322] font-medium' : 'text-secondary hover:bg-surface-raised hover:text-primary'
  }`;
  const className =
    variant === 'desktop' ? desktopClassName : variant === 'sidebar' ? sidebarClassName : mobileClassName;

  return (
    <Link
      to={item.route}
      data-nav-id={item.id}
      role={variant === 'desktop' ? 'menuitem' : undefined}
      title={isLocked ? lockedHint : undefined}
      aria-label={isLocked ? lockedHint : undefined}
      aria-current={active ? 'page' : undefined}
      className={className}
      onClick={onNavigate}
    >
      <span className="mr-2 relative inline-flex items-center">
        <Icon />
        {BadgeComponent ? <BadgeComponent /> : null}
      </span>
      <span>{label}</span>
      {isLocked && (
        <span className="ml-2 text-muted" aria-hidden="true">
          <IconLock />
        </span>
      )}
    </Link>
  );
};

/** Standard-Trigger-Inhalt (Icon + Label) für die drei generischen Dropdowns (Neu/Dokumente/Verwaltung). Das Konto-Dropdown übergibt stattdessen einen eigenen Avatar-Trigger. */
export const DefaultSectionTrigger: React.FC<{ icon: ComponentType; label: string }> = ({
  icon: Icon,
  label,
}) => (
  <>
    <span className="mr-1">
      <Icon />
    </span>
    <span>{label}</span>
  </>
);

interface DesktopNavDropdownProps {
  section: NavSection;
  ctx: NavContext;
  isOpen: boolean;
  onToggle: () => void;
  menuRef: RefObject<HTMLDivElement | null>;
  triggerContent: ReactNode;
  triggerAriaLabel?: string;
  isTriggerActive: boolean;
  align?: 'left' | 'right';
  panelWidthClassName?: string;
  extraItems?: ReactNode;
  excludeItemIds?: readonly string[];
  highlightActiveItems?: boolean;
  badgeRegistry?: NavBadgeRegistry;
}

/**
 * Desktop-Dropdown für EINE `NavSection`. `section.items` ist bereits das
 * Ergebnis von `resolveVisibleTree` (hidden-gefiltert); `excludeItemIds`
 * nimmt zusätzlich Items heraus, deren Verhalten außerhalb des generischen
 * Renderers abgebildet wird (Konto-Dropdown, s. Modul-Kopf). Rendert `null`,
 * wenn nach dem Filter weder Items noch `extraItems` übrig bleiben.
 */
export const DesktopNavDropdown: React.FC<DesktopNavDropdownProps> = ({
  section,
  ctx,
  isOpen,
  onToggle,
  menuRef,
  triggerContent,
  triggerAriaLabel,
  isTriggerActive,
  align = 'left',
  panelWidthClassName = 'w-48',
  extraItems,
  excludeItemIds,
  highlightActiveItems = false,
  badgeRegistry,
}) => {
  const items = excludeItemIds
    ? section.items.filter((item) => !excludeItemIds.includes(item.id))
    : section.items;
  if (items.length === 0 && !extraItems) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={onToggle}
        aria-label={triggerAriaLabel}
        aria-expanded={isOpen}
        data-nav-id={`section-${section.id}`}
        className={`flex items-center text-sm font-medium ${
          isTriggerActive ? 'text-accent-ink' : 'text-secondary hover:text-primary'
        } transition`}
      >
        {triggerContent}
        <span className="ml-1 h-3 w-3">
          <IconChevronDown />
        </span>
      </button>

      {isOpen && (
        <div
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} mt-2 ${panelWidthClassName} rounded-md shadow-lg bg-surface-raised ring-1 ring-black ring-opacity-5 z-50`}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            {items.map((item) => (
              <NavItemLink
                key={item.id}
                item={item}
                ctx={ctx}
                variant="desktop"
                highlightActive={highlightActiveItems}
                badgeRegistry={badgeRegistry}
              />
            ))}
            {extraItems}
          </div>
        </div>
      )}
    </div>
  );
};

interface MobileNavGroupProps {
  section: NavSection;
  ctx: NavContext;
  groupLabel: string;
  groupIcon: ComponentType;
  onNavigate: () => void;
  extraItems?: ReactNode;
  excludeItemIds?: readonly string[];
  badgeRegistry?: NavBadgeRegistry;
  bordered?: boolean;
}

/**
 * Mobile-Gruppe für EINE `NavSection`: statischer Header + Item-Liste — KEIN
 * eigenes Auf-/Zuklappen (Bestandsverhalten: die Gruppen sind vollständig
 * sichtbar, solange das Hamburger-Menü offen ist; nur DAS wird auf-/zugeklappt,
 * s. `MainLayout.tsx` `mobileMenuOpen`). `onNavigate` schließt das Hamburger-
 * Menü beim Klick auf einen Eintrag (Bestandsverhalten).
 */
export const MobileNavGroup: React.FC<MobileNavGroupProps> = ({
  section,
  ctx,
  groupLabel,
  groupIcon: GroupIcon,
  onNavigate,
  extraItems,
  excludeItemIds,
  badgeRegistry,
  bordered = true,
}) => {
  const items = excludeItemIds
    ? section.items.filter((item) => !excludeItemIds.includes(item.id))
    : section.items;
  if (items.length === 0 && !extraItems) return null;

  return (
    <div className={bordered ? 'border-b border-hairline pb-2 mb-2' : undefined}>
      <div className="text-sm font-medium text-primary px-2 py-1 flex items-center">
        <span className="mr-2">
          <GroupIcon />
        </span>{' '}
        {groupLabel}
      </div>
      {items.map((item) => (
        <NavItemLink
          key={item.id}
          item={item}
          ctx={ctx}
          variant="mobile"
          onNavigate={onNavigate}
          badgeRegistry={badgeRegistry}
        />
      ))}
      {extraItems}
    </div>
  );
};
